<script>
import draggable from 'vuedraggable'
import {
  moveDown,
  moveItem,
  moveUp,
  removeAt,
} from '@/helpers/reorderable-array-helpers'

const listInstances = []
const externalDragClassNames = [
  'draggable-list--drag-over',
  'draggable-list--drop-indicator',
  'draggable-list--hide-ghost',
]

let activeExternalTarget = null
let activeDrag = null

const groupPolicyProp = {
  type: [Boolean, Array],
  default: false,
  validator(value) {
    if (typeof value === 'boolean') return true
    return (
      Array.isArray(value) && value.every((group) => typeof group === 'string')
    )
  },
}

export default {
  name: 'BaseDraggableList',
  components: { draggable },
  props: {
    items: {
      type: Array,
      required: true,
    },
    itemKey: {
      type: String,
      default: 'id',
    },
    group: {
      type: String,
      required: true,
    },
    /** Source groups this list accepts drops from. Empty means no external drops. */
    acceptsGroups: {
      type: Array,
      default: () => [],
    },
    /** Enables dragging items out of / around this list. Default false by design. */
    dragEnabled: {
      type: Boolean,
      default: false,
    },
    /** Enables internal reordering. Requires drag-enabled. */
    reorderable: {
      type: Boolean,
      default: false,
    },
    /** Temporarily blocks external drop acceptance without changing accepts-groups. */
    dropDisabled: {
      type: Boolean,
      default: false,
    },
    /** true = clone when dragged to any target; array = clone only for those target groups. */
    cloneOnDrag: groupPolicyProp,
    /** true = use blue line ghost for any accepted source; array = only for those source groups. */
    dropIndicator: groupPolicyProp,
    /** true = hide drop ghost for any accepted source; array = only for those source groups. */
    hideGhost: groupPolicyProp,
    /** true = reject duplicate itemKey drops from any source; array = only for those source groups. */
    rejectDuplicateDrops: groupPolicyProp,
    /** true = emit change after external additions; array = only for those source groups. */
    commitExternalDrops: groupPolicyProp,
    /** true = emit change after external removals; array = only for those target groups. */
    commitExternalRemovals: groupPolicyProp,
    /** true = skip leave transition while dragging; array = only while over those target groups. */
    disableLeaveAnimation: groupPolicyProp,
    /** Fills the list height so empty space can still receive drops. */
    fillDropArea: {
      type: Boolean,
      default: false,
    },
    dragHandle: {
      type: String,
      default: null,
    },
    animation: {
      type: Number,
      default: 180,
    },
  },
  data() {
    return {
      workingItems: this.cloneItems(this.items),
      isDraggingSelf: false,
      isExternalDragOver: false,
      externalDragSourceGroup: null,
      externalDragItem: null,
      currentDragTargetGroup: null,
      syncFromPropsQueued: false,
    }
  },
  computed: {
    acceptsExternalDrops() {
      return !this.dropDisabled && this.acceptsGroups.length > 0
    },
    canReorder() {
      return this.dragEnabled && this.reorderable
    },
    shouldFillDropArea() {
      return this.fillDropArea || this.acceptsExternalDrops
    },
    shouldShowDropIndicator() {
      return (
        this.isExternalDragOver &&
        this.policyMatches(this.dropIndicator, this.externalDragSourceGroup)
      )
    },
    shouldHideDropGhost() {
      return (
        this.isExternalDragOver &&
        !this.shouldShowDropIndicator &&
        this.policyMatches(this.hideGhost, this.externalDragSourceGroup)
      )
    },
    shouldUseInstantLeave() {
      const targetGroup = this.currentDragTargetGroup || this.group

      return (
        this.isDraggingSelf &&
        this.policyMatches(this.disableLeaveAnimation, targetGroup)
      )
    },
    sortableGroup() {
      return {
        name: this.group,
        pull: this.resolvePull,
        put: this.resolvePut,
      }
    },
    dragOptions() {
      const options = {
        animation: this.animation,
        disabled: !this.dragEnabled && !this.acceptsExternalDrops,
        draggable: '.draggable-list-item',
        ghostClass: 'draggable-list-item--ghost',
        chosenClass: 'draggable-list-item--chosen',
        dragClass: 'draggable-list-item--dragging',
        group: this.sortableGroup,
        move: this.onMove,
        noTransitionOnDrag: this.policyMatches(
          this.disableLeaveAnimation,
          this.currentDragTargetGroup || this.group
        ),
        sort: this.canReorder,
      }

      if (this.dragHandle) {
        options.handle = this.dragHandle
      }

      return options
    },
  },
  watch: {
    items: {
      handler(items) {
        if (this.syncFromPropsQueued) return

        this.workingItems = this.cloneItems(items)
      },
    },
  },
  mounted() {
    listInstances.push(this)
  },
  beforeDestroy() {
    this.clearExternalDragTarget()
    this.unregisterListInstance()
  },
  methods: {
    cloneItems(items) {
      return Array.isArray(items) ? items.slice() : []
    },
    policyMatches(policy, group) {
      if (policy === true) return true
      if (!Array.isArray(policy)) return false

      return Boolean(group) && policy.includes(group)
    },
    getSortableGroupName(sortable) {
      return sortable?.options?.group?.name || null
    },
    getItemKey(item, fallbackIndex = null) {
      if (!item) return fallbackIndex

      const key = item[this.itemKey]
      return key == null ? fallbackIndex : key
    },
    unregisterListInstance() {
      const index = listInstances.indexOf(this)

      if (index >= 0) {
        listInstances.splice(index, 1)
      }
    },
    emitChange(updated) {
      this.$emit('change', this.cloneItems(updated))
    },
    queueSyncFromProps() {
      if (this.syncFromPropsQueued) return

      this.syncFromPropsQueued = true
      this.$nextTick(() => {
        this.workingItems = this.cloneItems(this.items)
        this.syncFromPropsQueued = false
      })
    },
    syncFromProps() {
      this.workingItems = this.cloneItems(this.items)
      this.syncFromPropsQueued = false
    },
    getExternalDragClassNames(sourceGroup) {
      const classNames = ['draggable-list--drag-over']

      if (this.policyMatches(this.dropIndicator, sourceGroup)) {
        classNames.push('draggable-list--drop-indicator')
        return classNames
      }

      if (this.policyMatches(this.hideGhost, sourceGroup)) {
        classNames.push('draggable-list--hide-ghost')
      }

      return classNames
    },
    applyExternalDragClasses(sourceGroup) {
      this.removeExternalDragClasses()
      this.getExternalDragClassNames(sourceGroup).forEach((className) => {
        this.$el.classList.add(className)
      })
    },
    removeExternalDragClasses() {
      externalDragClassNames.forEach((className) => {
        this.$el.classList.remove(className)
      })
    },
    remove(index) {
      if (!this.dragEnabled) return

      this.emitChange(removeAt(this.items, index))
    },
    moveUp(index) {
      if (!this.canReorder) return

      this.emitChange(moveUp(this.items, index))
    },
    moveDown(index) {
      if (!this.canReorder) return

      this.emitChange(moveDown(this.items, index))
    },
    resolvePull(to) {
      if (!this.dragEnabled) return false

      const targetGroup = this.getSortableGroupName(to)

      if (this.policyMatches(this.cloneOnDrag, targetGroup)) {
        return 'clone'
      }

      return true
    },
    resolvePut(to, from) {
      const sourceGroup = this.getSortableGroupName(from)

      return this.canAcceptDragFrom(sourceGroup)
    },
    canAcceptDragFrom(sourceGroup) {
      if (!this.acceptsExternalDrops) return false
      if (!sourceGroup) return false

      return this.acceptsGroups.includes(sourceGroup)
    },
    hasItem(item, items = this.workingItems) {
      if (!item) return false

      const incomingKey = this.getItemKey(item)
      if (incomingKey == null) return false

      return items.some(
        (currentItem) => this.getItemKey(currentItem) === incomingKey
      )
    },
    shouldRejectDuplicateFrom(sourceGroup, item, items = this.workingItems) {
      return (
        this.policyMatches(this.rejectDuplicateDrops, sourceGroup) &&
        this.hasItem(item, items)
      )
    },
    onDragStart(event) {
      const item = this.workingItems[event.oldIndex]

      this.isDraggingSelf = true
      this.currentDragTargetGroup = this.group
      activeDrag = {
        item,
        source: this,
        sourceGroup: this.group,
        targetGroup: this.group,
      }

      this.$emit('drag-start', {
        item,
        sourceGroup: this.group,
      })
    },
    onDragEnd() {
      const payload = activeDrag
        ? {
            item: activeDrag.item,
            sourceGroup: activeDrag.sourceGroup,
            targetGroup: activeDrag.targetGroup,
          }
        : {
            item: null,
            sourceGroup: this.group,
            targetGroup: this.currentDragTargetGroup,
          }

      this.isDraggingSelf = false
      this.currentDragTargetGroup = null
      this.clearExternalDragTarget()
      activeDrag = null
      this.$emit('drag-end', payload)
    },
    onItemBeforeLeave(element) {
      if (this.shouldUseInstantLeave) return

      element.style.top = `${element.offsetTop}px`
      element.style.left = `${element.offsetLeft}px`
      element.style.width = `${element.offsetWidth}px`
    },
    onItemAfterLeave(element) {
      element.style.top = null
      element.style.left = null
      element.style.width = null
    },
    onDragChange(event) {
      if (event.added) {
        this.onExternalAdded(event.added)
        return
      }

      if (event.removed) {
        this.onExternalRemoved(event.removed)
        return
      }

      if (event.moved) {
        this.emitChange(
          moveItem(this.items, event.moved.oldIndex, event.moved.newIndex)
        )
      }
    },
    onExternalAdded(added) {
      const sourceGroup =
        this.externalDragSourceGroup || activeDrag?.sourceGroup
      if (
        this.shouldRejectDuplicateFrom(sourceGroup, added.element, this.items)
      ) {
        this.syncFromProps()
        this.clearExternalDragTarget()
        return
      }

      const shouldCommitDrop = this.policyMatches(
        this.commitExternalDrops,
        sourceGroup
      )
      const shouldHideAddedItem = this.policyMatches(
        this.hideGhost,
        sourceGroup
      )
      const payload = {
        item: added.element,
        sourceGroup,
        targetGroup: this.group,
        targetIndex: added.newIndex,
        items: this.cloneItems(this.workingItems),
      }

      this.$emit('external-drop', payload)
      this.clearExternalDragTarget()

      if (shouldCommitDrop) {
        this.emitChange(this.workingItems)
        return
      }

      if (shouldHideAddedItem) {
        this.syncFromProps()
        return
      }

      this.queueSyncFromProps()
    },
    onExternalRemoved(removed) {
      const targetGroup = activeDrag?.targetGroup || this.currentDragTargetGroup
      const payload = {
        item: removed.element,
        sourceGroup: this.group,
        targetGroup,
        oldIndex: removed.oldIndex,
        items: this.cloneItems(this.workingItems),
      }

      this.$emit('external-remove', payload)

      if (this.policyMatches(this.commitExternalRemovals, targetGroup)) {
        this.emitChange(this.workingItems)
        return
      }

      this.queueSyncFromProps()
    },
    onMove(event) {
      const target = this.getTargetListFromMoveEvent(event)

      if (!target || target === this) {
        this.currentDragTargetGroup = this.group
        this.clearExternalDragTarget()
        return true
      }

      const sourceGroup = this.group
      const item = event.draggedContext?.element || activeDrag?.item

      if (!target.canAcceptDragFrom(sourceGroup)) {
        this.clearExternalDragTarget()
        return false
      }

      if (target.shouldRejectDuplicateFrom(sourceGroup, item)) {
        this.clearExternalDragTarget()
        return false
      }

      this.currentDragTargetGroup = target.group
      if (activeDrag) {
        activeDrag.targetGroup = target.group
      }

      this.setExternalDragTarget(target, {
        item,
        source: this,
        sourceGroup,
      })
      return true
    },
    getTargetListFromMoveEvent(event) {
      const relatedParent = event.relatedContext?.component?.$parent

      if (
        relatedParent &&
        relatedParent.$options?.name === this.$options.name
      ) {
        return relatedParent
      }

      return listInstances.find((instance) => {
        const draggableEl = instance.$refs.draggable?.$el

        if (!draggableEl || !event.to) return false

        return draggableEl === event.to || draggableEl.contains(event.to)
      })
    },
    setExternalDragTarget(target, dragContext) {
      if (activeExternalTarget === target) return

      this.clearExternalDragTarget()
      activeExternalTarget = target
      target.startExternalDragState(dragContext)
    },
    clearExternalDragTarget() {
      if (!activeExternalTarget) return

      activeExternalTarget.clearExternalDragState()
      activeExternalTarget = null
    },
    startExternalDragState({ item, source, sourceGroup }) {
      if (
        this.isExternalDragOver &&
        this.externalDragSourceGroup === sourceGroup &&
        this.externalDragItem === item
      ) {
        return
      }

      this.isExternalDragOver = true
      this.externalDragSourceGroup = sourceGroup
      this.externalDragItem = item
      this.applyExternalDragClasses(sourceGroup)
      this.$emit('external-drag-enter', {
        item,
        source,
        sourceGroup,
        targetGroup: this.group,
      })
    },
    clearExternalDragState() {
      if (!this.isExternalDragOver) return

      const payload = {
        item: this.externalDragItem,
        sourceGroup: this.externalDragSourceGroup,
        targetGroup: this.group,
      }

      this.isExternalDragOver = false
      this.externalDragSourceGroup = null
      this.externalDragItem = null
      this.removeExternalDragClasses()
      this.$emit('external-drag-leave', payload)
    },
  },
}
</script>

<template>
  <div
    class="draggable-list p-relative"
    :class="{
      'draggable-list--fill-append': shouldFillDropArea,
      'draggable-list--drag-over': isExternalDragOver,
      'draggable-list--reorderable': canReorder,
      'draggable-list--drop-indicator': shouldShowDropIndicator,
      'draggable-list--hide-ghost': shouldHideDropGhost,
    }"
  >
    <div v-if="workingItems.length === 0" class="draggable-list-empty">
      <slot name="empty" />
    </div>

    <draggable
      ref="draggable"
      :list="workingItems"
      v-bind="dragOptions"
      class="draggable-list-sortable"
      @change="onDragChange"
      @start="onDragStart"
      @end="onDragEnd"
    >
      <transition-group
        type="transition"
        name="vertical-list-item"
        tag="div"
        class="draggable-list-items"
        @before-leave="onItemBeforeLeave"
        @after-leave="onItemAfterLeave"
      >
        <div
          v-for="(item, index) in workingItems"
          :key="getItemKey(item, index)"
          class="draggable-list-item pb-2"
          :class="{
            'draggable-list-item--draggable': dragEnabled,
            'draggable-list-item--instant-leave': shouldUseInstantLeave,
          }"
        >
          <slot
            :item="item"
            :index="index"
            :is-first="index === 0"
            :is-last="index === workingItems.length - 1"
            :is-external-drag-over="isExternalDragOver"
            :external-drag-source-group="externalDragSourceGroup"
            :move-up="() => moveUp(index)"
            :move-down="() => moveDown(index)"
            :remove="() => remove(index)"
          />
        </div>
      </transition-group>
    </draggable>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_vue-transitions.scss';
@import '@/styles/_r2b-colours.scss';

$draggable-list-ghost-height: 5px;

.draggable-list {
  overflow-x: clip;

  &--fill-append {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  &--drop-indicator {
    .draggable-list-item--ghost {
      z-index: 2;
      flex: 0 0 $draggable-list-ghost-height;
      height: $draggable-list-ghost-height;
      min-height: $draggable-list-ghost-height;
      overflow: visible;
      pointer-events: none;
      opacity: 1;

      ::v-deep > * {
        visibility: hidden;
      }

      &::before {
        position: absolute;
        top: 50%;
        right: 0;
        left: 0;
        height: $draggable-list-ghost-height;
        content: '';
        background: $brand-blue-400;
        border-radius: 8px;
        transform: translateY(-50%);
      }
    }
  }

  &--hide-ghost {
    .draggable-list-item--ghost {
      width: 0 !important;
      height: 0 !important;
      min-height: 0;
      max-height: 0;
      padding-bottom: 0 !important;
      margin: 0 !important;
      overflow: hidden;
      pointer-events: none;
      visibility: hidden;
      opacity: 0;
    }
  }
}

.draggable-list-empty {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 0;
  pointer-events: none;
}

.draggable-list-sortable {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-height: 100%;
}

.draggable-list-items {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.draggable-list,
.draggable-list-sortable,
.draggable-list-items,
.draggable-list-item {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.draggable-list-item {
  &:not(.vertical-list-item-leave-active) {
    position: relative;
  }

  &--draggable {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  &--chosen {
    cursor: grabbing;
  }

  &--clone {
    pointer-events: none;
  }

  &--instant-leave {
    &.vertical-list-item-leave-to,
    &.vertical-list-item-leave-active {
      position: relative;
      width: auto;
      opacity: 1;
      transition: none;
      transform: none;
    }
  }

  &--dragging {
    opacity: 0.75;
  }
}
</style>
